import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiDownload } from 'react-icons/fi';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { useClient, useQuery } from 'jsonapi-react';
import moment from 'moment';
import { Loader } from '@/components/custom/loader';
import ReportForm from '@/components/custom/report-form';
import usePermission from '@/hooks/usePermission';
import toast from 'react-hot-toast';
import TransmissionsTable from './TransmissionsTable';
import TransmissionDetailsTable from './TransmissionDetailsTable';
import { EmptyState } from '@/components/custom/empty-state';
import { getReportFileName } from '@/utils/getReportFileName';

export function LiveTransmissionsReport() {
  const navigate = useNavigate();
  const session = JSON.parse(localStorage.getItem('session'));
  const client = useClient();

  const [selectedTransmission, setSelectedTransmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [kpisData, setKpisData] = useState();
  const [transmissionDetails, setTransmissionDetails] = useState([]);
  const [detailKpis, setDetailKpis] = useState();

  const { data: courses = [], isLoading: isLoadingCourses } = useQuery('/courses/simple');

  const filters = useFormik({
    initialValues: {
      course: 'blank',
      'start-date': moment().subtract(30, 'days').format('YYYY-MM-DD'),
      'end-date': moment().format('YYYY-MM-DD')
    }
  });

  const getReportData = async () => {
    setLoading(true);

    let url = `reports/live_classroom_transmission_hours?`;

    if (filters.values['start-date']) {
      url += `&initial_date=${filters.values['start-date']}`;
    }
    if (filters.values['end-date']) {
      url += `&end_date=${filters.values['end-date']}`;
    }
    if (filters.values.course !== 'blank') {
      url += `&course_ids=${filters.values.course}`;
    }

    try {
      const { data, error } = await client.fetch(url);
      if (error?.message === 'Parâmetros de data inválidos') {
        toast.error('O intervalo entre a data inicial e final deve ser de no máximo 30 dias');
        setLoading(false);
        setKpisData([]);
        setReportData([]);
        return;
      }
      setReportData(data.items);
      setKpisData(data.kpis);
    } catch (error) {
      console.log(error);
      toast.error('Erro ao buscar dados do relatório.');
    }
    setLoading(false);
  };

  const getTransmissionDetail = async () => {
    setLoading(true);

    let url = `reports/live_classroom_transmission_hours/${selectedTransmission?.['title_id']}?`;

    if (filters.values.course !== 'blank') {
      url += `&course_ids=${filters.values.course}`;
    }

    try {
      const { data } = await client.fetch(url);

      setTransmissionDetails(data.items);
      setDetailKpis(data.kpis);
    } catch (error) {
      console.log(error);
      toast.error('Erro ao buscar dados do relatório.');
    }
    setLoading(false);
  };

  const handleDownload = () => {
    setIsDownloading(true);

    let url = selectedTransmission ? `/reports/live_classroom_transmission_hours/${selectedTransmission?.['title_id']}.xlsx?` : `/reports/live_classroom_transmission_hours.xlsx?`;

    if (filters.values['start-date']) {
      url += `&initial_date=${filters.values['start-date']}`;
    }
    if (filters.values['end-date']) {
      url += `&end_date=${filters.values['end-date']}`;
    }
    if (filters.values.course !== 'blank') {
      url += `&course_ids=${filters.values.course}`;
    }

    fetch(`${process.env.REACT_APP_DEV_URL}${url}`, {
      headers: {
        Authorization: `Bearer ${session?.user?.authToken}`
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Erro ao baixar o relatório');
        }

        const disposition = response.headers.get('content-disposition');
        const fileName = getReportFileName(disposition);

        return response.blob().then(blob => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Relatório baixado com sucesso!');
      })
      .catch(error => {
        console.error('Erro no download:', error);
        toast.error('Erro ao baixar o relatório');
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const kpis = [
    {
      id: 1,
      title: 'Total de transmissões',
      value: kpisData?.['transmissions_count'],
      variation: 'report-kpi-card--video'
    },
    {
      id: 2,
      title: 'Horas totais transmitidas',
      value: kpisData?.['total_transmitted_hours'],
      variation: 'report-kpi-card--clock'
    }
  ];

  useEffect(() => {
    if (selectedTransmission) {
      getTransmissionDetail();
    } else {
      getReportData();
    }
  }, [filters.values]);

  useEffect(() => {
    if (selectedTransmission) {
      getTransmissionDetail();
    } else {
      getReportData();
    }
  }, [selectedTransmission]);

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">
          <button
            className="card__icon-wrapper"
            aria-label="Voltar"
            title="Voltar"
            onClick={() => navigate(-1)}
          >
            <FiChevronLeft className="card__icon" />
          </button>
          Transmissão ao Vivo
        </h1>
        <p className="report__description">Consolida informações das transmissões ao vivo por aula, com total de aulas e horas transmitidas, permitindo detalhamento por aula.</p>
      </header>

      <div className="report__body">
        <div className="report__filter">
          <ReportForm.Select
            label={t('exams.selectCourse')}
            value={filters.values.course}
            blankLabel={t('metrics.blankLabelSelect')}
            onChange={e => filters.setFieldValue('course', e.target.value)}
            options={courses.map(o => ({
              id: o.id,
              name: o.title
            }))}
          />
          <div className="report-form__datepicker">
            <input
              className="report-form__control"
              id="start-date"
              name="start-date"
              type="date"
              value={moment(filters.values['start-date']).format('YYYY-MM-DD')}
              onChange={e => filters.setFieldValue('start-date', e.target.value)}
              title="Data de início"
              aria-label="Data de início"
            />
            <input
              className="report-form__control"
              id="start-date"
              name="start-date"
              type="date"
              value={moment(filters.values['end-date']).format('YYYY-MM-DD')}
              onChange={e => filters.setFieldValue('end-date', e.target.value)}
              min={moment(filters.values['start-date']).format('YYYY-MM-DD')}
              title="Data de fim"
              aria-label="Data de fim"
            />
          </div>
        </div>

        {loading && <Loader />}

        {!loading && reportData.length === 0 && (
          <div className="flex flex-col items-center py-28">
            <EmptyState
              variant="plain"
              type="data"
              text="Ainda não temos dados para mostrar"
            />
          </div>
        )}

        {!loading && reportData.length > 0 && (
          <>
            <div className="report-widget">
              <div className="report-widget__header">
                <h3 className="report-widget__title">Visão geral</h3>
              </div>

              <div className="report-widget__body">
                <div className="report-kpi-card-wrapper">
                  {kpis.map(kpi => (
                    <div
                      className={`report-kpi-card ${kpi.variation}`}
                      key={kpi.id}
                    >
                      <h3 className="report-kpi-card__title font-semibold text-primary-dark">{kpi.title}</h3>
                      <p
                        className="report-kpi-card__value"
                        title={kpi.value}
                      >
                        {kpi.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="report-widget">
              <div className="report-widget__header">
                <h3 className="report-widget__title">Aulas ao vivo</h3>

                <div className="report-widget__filter">
                  {usePermission('Download de relatórios', 'Relatórios') && (
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={handleDownload}
                      disabled={isDownloading}
                    >
                      <FiDownload className="btn__icon" /> Fazer download
                    </button>
                  )}
                </div>
              </div>

              {!selectedTransmission && (
                <TransmissionsTable
                  data={reportData}
                  setSelectedTransmission={setSelectedTransmission}
                />
              )}
              {selectedTransmission && (
                <TransmissionDetailsTable
                  data={transmissionDetails}
                  setSelectedTransmission={setSelectedTransmission}
                  selectedTransmission={selectedTransmission}
                  kpis={detailKpis}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
